import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import ChartDisplay from '../ChartDisplay/index.js';
import Spinner from '../shared/Spinner/index.js';
import {
  InputDropdown,
  SearchableDropdown,
  InputMultiDropdown,
  InputRange,
} from '../shared/inputs/index.js';
import { RangeFormat } from '../shared/inputs/Range';
import { generateDashboard } from '../../gql';
import { CollapsibleSection } from './CollapsibleSection.js';
import { schools } from './schools.js';
import Style from './style.module.css';

const zipCodes = [
  '19102',
  '19103',
  '19104',
  '19106',
  '19107',
  '19109',
  '19111',
  '19112',
  '19114',
  '19115',
  '19116',
  '19118',
  '19119',
  '19120',
  '19121',
  '19122',
  '19123',
  '19124',
  '19125',
  '19126',
  '19127',
  '19128',
  '19129',
  '19130',
  '19131',
  '19132',
  '19133',
  '19134',
  '19135',
  '19136',
  '19137',
  '19138',
  '19139',
  '19140',
  '19141',
  '19142',
  '19143',
  '19144',
  '19145',
  '19146',
  '19147',
  '19148',
  '19149',
  '19150',
  '19151',
  '19152',
  '19153',
  '19154',
];

const neighborhoods = [
  'Airport-Navy Yard',
  'Bustleton',
  'Center City East',
  'Center City West',
  'Chestnut Hill-West Mt. Airy',
  'Cobbs Creek',
  'East Falls-Westside',
  'East Mt. Airy',
  'Eastwick-Elmwood',
  'Fairmount-Spring Garden',
  'Frankford',
  'Germantown',
  'Grays Ferry-Passyunk',
  'Haddington-Overbrook',
  'Hunting Park-Fairhill',
  'Juniata Park-Harrowgate',
  'Lawndale-Crescentville',
  'Logan',
  'Lower Kensington',
  'Mayfair-Holmesburg',
  'Mill Creek-Parkside',
  'NE Airport',
  'Nicetown-Tioga',
  'Northern Liberties-West Kensington',
  'Oak Lane - Fernrock',
  'Ogontz',
  'Olney-Feltonville',
  'Overbrook Park-Wynnefield Heights',
  'Oxford Circle',
  'Park',
  'Paschall-Kingsessing',
  'Pennsport-Queen Village',
  'Poplar-Temple',
  'Rhawnhurst-Fox Chase',
  'Richmond-Bridesburg',
  'Roxborough-Manayunk',
  'Schuylkill-Point Breeze',
  'Sharswood-Stanton',
  'Snyder-Whitman',
  'Somerton',
  'South Broad-Girard Estates',
  'Southwark-Bella Vista',
  'Strawberry Mansion',
  'Torresdale North',
  'Torresdale South-Pennypack Park',
  'University City',
  'Upper Kensington',
  'West Oak Lane-Cedarbrook',
  'Wissinoming-Tacony',
];

const dcWards = ['1', '2', '3', '4', '5', '6', '7', '8'].map((ward) => ({
  value: ward,
  label: `Ward ${ward}`,
}));

const councilDistrictOptions = Array.from({ length: 10 }, (_, index) => ({
  value: String(index + 1),
  label: `District ${index + 1}`,
}));

const parseCouncilDistrictNumbers = (values) => {
  const selected = (values || []).map((v) => String(v).trim()).filter(Boolean);
  if (selected.some((v) => v.toLowerCase() === 'all')) {
    return councilDistrictOptions.map((opt) => Number(opt.value));
  }
  return selected
    .map(Number)
    .filter(
      (district) =>
        Number.isInteger(district) && district >= 1 && district <= 10,
    );
};

const dcCviSites = [
  'Bellevue',
  'Brentwood',
  'Brightwood Park/Uptown',
  'Buena Vista Terrace',
  'Carver Langston',
  'Cedar Gardens',
  'Columbia Heights',
  'Congress Heights',
  'Eckington/Truxton Circle',
  'Fort Dupont/Stodert/37th',
  'Greenleaf',
  'Hartford',
  'Historic Anacostia/Fairlawn',
  'Kenilworth',
  'Langdon Park',
  'Lincoln Heights',
  'Marshall Heights',
  'Mayfair/Paradise',
  'Park View',
  'Petworth',
  'Richardson Dwellings',
  'Rosedale',
  'Shipley',
  'Sursum Corda/Ivy City',
  'Syphax Gardens',
  'Trenton Park & Wahler Place',
  'Trinidad and Arboretum',
  'Washington Highlands',
  'Woodland/Langston Lane',
];
const getEndOfCurrentMonth = () => {
  const now = new Date();
  const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  return endOfMonth.toISOString().split('T')[0];
};

const minutesToHHMMSS = (value) => {
  if (value === null || value === undefined || value === '') return undefined;

  let total = Number(value);
  if (total >= 1440) total = 1439;

  const hours = String(Math.floor(total / 60)).padStart(2, '0');
  const minutes = String(total % 60).padStart(2, '0');

  return `${hours}:${minutes}:00`;
};

const formatTimePickerLabel = (minutes) => {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  const period = h < 12 ? 'am' : 'pm';
  const displayH = h % 12 === 0 ? 12 : h % 12;
  return `${displayH}:${String(m).padStart(2, '0')}${period}`;
};

const TIME_PICKER_OPTIONS = Object.fromEntries(
  Array.from({ length: (24 * 60) / 15 }, (_, i) => {
    const minutes = i * 15;
    return [
      String(minutes),
      {
        label: formatTimePickerLabel(minutes),
        value: minutes,
        sortOrder: minutes,
      },
    ];
  }),
);

const parseTimePickerValue = (val) => {
  if (val === '') return '';
  const num = Number(val);
  if (Number.isNaN(num) || !TIME_PICKER_OPTIONS[String(num)]) return '';
  return num;
};

const normalizeRace = (race) => {
  if (!race) return undefined;

  if (Array.isArray(race)) {
    return race.filter(Boolean);
  }

  if (typeof race === 'string') {
    return race
      .split(',')
      .map((r) => r.trim())
      .filter(Boolean);
  }

  return undefined;
};

const ChartForm = () => {
  const [searchParams, setSearchParams] = useSearchParams({
    jurisdiction: 'PHILADELPHIA',
  });
  const endOfCurrentMonth = getEndOfCurrentMonth();
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [chartData, setChartData] = useState(null);
  const [pptInfo, setPptInfo] = useState({});
  const [subscribeConfig, setSubscribeConfig] = useState(null);
  const [openSections, setOpenSections] = useState({
    optional: false,
    advanced: false,
  });

  const toggleSection = (id) => {
    setOpenSections((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const getParam = (key) => searchParams.get(key) || '';
  const getMultiParam = (key) =>
    getParam(key)
      .split(',')
      .map((value) => value.trim())
      .filter(Boolean);

  const toSpaceSeparatedList = (values, quoteValues = false) =>
    values
      .map((value) => value.trim())
      .filter(Boolean)
      .map((value) => (quoteValues ? `"${value.replace(/"/g, '\\"')}"` : value))
      .join(' ');

  const updateParams = (updates) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);

      for (const [key, value] of Object.entries(updates)) {
        next.delete(key);

        if (value === undefined || value === null || value === '') {
          continue;
        }

        if (Array.isArray(value)) {
          next.set(key, value.length ? value.join(',') : '');
        } else {
          next.set(key, value);
        }
      }

      return next;
    });
  };
  const getArrayParam = (key) => {
    const val = searchParams.get(key);
    if (!val) return [];
    return val.split(',').filter(Boolean);
  };

  const handleRangeChange = (baseName, { min, max }) => {
    updateParams({
      [`${baseName}_min`]: min?.toString() ?? '',
      [`${baseName}_max`]: max?.toString() ?? '',
    });
  };

  const handleTimePickerChange = (key, value) => {
    const otherKey = key === 'time_min' ? 'time_max' : 'time_min';
    const updates = { [key]: value?.toString() ?? '' };

    if (value != null && value !== '' && !getParam(otherKey)) {
      updates[otherKey] = '0';
    }

    updateParams(updates);
  };

  const updateParam = (key, value) => updateParams({ [key]: value });

  const handleInputChange = (e) => {
    const { id, value } = e.target;
    updateParam(id, value);
  };

  const handleAreaFilterChange = (key, value) => {
    updateParams({
      zipCode: key === 'zipCode' ? value : '',
      neighborhood: key === 'neighborhood' ? value : '',
      councilDistricts: key === 'councilDistricts' ? value : '',
    });
  };

  const handleSchoolChange = (e) => {
    const { value } = e.target;
    const updates = { schoolName: value };

    if (
      value &&
      !getParam('blocksFromSchool_min') &&
      !getParam('blocksFromSchool_max')
    ) {
      updates.blocksFromSchool_min = '0';
      updates.blocksFromSchool_max = '100';
    }

    updateParams(updates);
  };

  const periodicityOptions = [
    { value: '', label: 'Select time interval' },
    { value: 'week', label: 'Weekly' },
    { value: 'month', label: 'Monthly' },
    { value: 'quarter', label: 'Quarterly' },
    { value: 'year', label: 'Yearly' },
    { value: 'total', label: 'Total (full date range)' },
  ];

  const isDC = () => getParam('jurisdiction') === 'DC';

  const validateDashboardForm = (currentForm) => {
    if (!currentForm.startDate || !currentForm.endDate) {
      setError('Please fill in both start and end dates');
      return false;
    }
    if (!currentForm.periodicity) {
      setError('Please select a periodicity');
      return false;
    }

    if (currentForm.jurisdiction === 'DC') {
      return true;
    }

    const areaSelections = [
      currentForm.zipCode,
      currentForm.neighborhood,
      currentForm.councilDistricts,
    ].filter(Boolean);

    if (areaSelections.length === 0) {
      setError(
        'Please fill in either ZIP Code, Neighborhood, or Council District',
      );
      return false;
    }

    if (areaSelections.length > 1) {
      setError(
        'Please fill in only one area filter: ZIP Code, Neighborhood, or Council District',
      );
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');
    setIsLoading(true);
    setChartData(null);
    setSubscribeConfig(null);

    const jurisdiction = getParam('jurisdiction') || 'PHILADELPHIA';
    const currentForm = {
      jurisdiction,
      zipCode: getParam('zipCode'),
      neighborhood: getParam('neighborhood'),
      councilDistricts: getParam('councilDistricts'),
      zipCodes: getMultiParam('zipCode'),
      neighborhoods: getMultiParam('neighborhood'),
      councilDistrictsList: getMultiParam('councilDistricts'),
      startDate: getParam('startDate'),
      endDate: getParam('endDate'),
      periodicity: getParam('periodicity'),
    };

    const optionalParams =
      jurisdiction === 'DC'
        ? {
            ward: getMultiParam('ward')
              .filter((w) => w.toLowerCase() !== 'all')
              .map((w) => Number(w))
              .filter((w) => !Number.isNaN(w)),
            cvi_site: getMultiParam('cviSite').filter(
              (s) => s.toLowerCase() !== 'all',
            ),
            min_blocks_from_another_shooting: getParam('blocksFromShooting_min')
              ? Number(getParam('blocksFromShooting_min'))
              : undefined,
            max_blocks_from_another_shooting: getParam('blocksFromShooting_max')
              ? Number(getParam('blocksFromShooting_max'))
              : undefined,
          }
        : {
            shooting_type: getParam('shootingType')?.toLowerCase(),
            incident_type: getParam('incidentType') || undefined,
            sex: getParam('sex'),
            race: normalizeRace(getArrayParam('race')),
            age_min: getParam('age_min')
              ? Number(getParam('age_min'))
              : undefined,
            age_max: getParam('age_max')
              ? Number(getParam('age_max'))
              : undefined,
            min_blocks_from_school: getParam('blocksFromSchool_min')
              ? Number(getParam('blocksFromSchool_min'))
              : undefined,
            max_blocks_from_school: getParam('blocksFromSchool_max')
              ? Number(getParam('blocksFromSchool_max'))
              : undefined,
            school_name: getParam('schoolName'),
            time_min: minutesToHHMMSS(getParam('time_min')),
            time_max: minutesToHHMMSS(getParam('time_max')),
            council_districts: parseCouncilDistrictNumbers(
              getMultiParam('councilDistricts'),
            ),
          };

    const isValid = validateDashboardForm(currentForm);
    const query = generateDashboard;

    const input = {
      jurisdiction,
      startDate: currentForm.startDate,
      endDate: currentForm.endDate,
      periodicity: currentForm.periodicity,
      ...(jurisdiction === 'DC'
        ? {}
        : {
            zipCode: toSpaceSeparatedList(currentForm.zipCodes),
            neighborhood: toSpaceSeparatedList(currentForm.neighborhoods, true),
            council_districts: parseCouncilDistrictNumbers(
              currentForm.councilDistrictsList,
            ),
          }),
    };

    if (!input.council_districts?.length) {
      delete input.council_districts;
    }

    Object.entries(optionalParams).forEach(([key, value]) => {
      const isEmpty =
        value === '' ||
        value === null ||
        value === undefined ||
        (Array.isArray(value) && value.length === 0);

      if (!isEmpty) {
        input[key] = value;
      }
    });

    const variables = { input };
    const resultKey = 'generateDashboard';

    if (!isValid) {
      setIsLoading(false);
      return;
    }

    try {
      const response = await axios.post(process.env.REACT_APP_GRAPHQL_URI, {
        query,
        variables,
      });

      if (response.data.errors) {
        throw new Error(response.data.errors[0].message);
      }

      if (response.data.data?.[resultKey]) {
        setMessage(response.data.data[resultKey].message);
        setChartData(response.data.data[resultKey]);
        setPptInfo(input);
        setSubscribeConfig({
          dashboardInput: input,
          queryString: searchParams.toString(),
        });
      }
    } catch (error) {
      console.error('Error details:', error);
      setError('Failed to generate charts');
    } finally {
      setIsLoading(false);
    }
  };

  const renderDashboardFields = () => (
    <div>
      <div className={Style.formRow}>
        <InputDropdown
          inputList={{
            PHILADELPHIA: {
              label: 'Philadelphia Shootings Report',
              value: 'PHILADELPHIA',
            },
            // DC: {
            //   label: 'DC Shootings Report',
            //   value: 'DC',
            // },
          }}
          containerStyle={{ marginBottom: '1em' }}
          labelText="Report"
          name="jurisdiction"
          value={getParam('jurisdiction') || 'PHILADELPHIA'}
          onChange={({ jurisdiction }) => {
            updateParams({
              jurisdiction,
              ...(jurisdiction === 'DC'
                ? {
                    zipCode: '',
                    neighborhood: '',
                    councilDistricts: '',
                    shootingType: '',
                    incidentType: '',
                    sex: '',
                    race: '',
                    age_min: '',
                    age_max: '',
                    time_min: '',
                    time_max: '',
                    blocksFromSchool_min: '',
                    blocksFromSchool_max: '',
                    schoolName: '',
                  }
                : {
                    ward: '',
                    cviSite: '',
                    blocksFromShooting_min: '',
                    blocksFromShooting_max: '',
                  }),
            });
          }}
        />
      </div>
      <div className={Style.quickDates}>
        <div className={Style.formGroup}>Quick dates: </div>
        <div
          className={Style.quickDateLink}
          onClick={() => {
            updateParams({
              startDate: new Date('2026-01-01').toISOString().split('T')[0],
              endDate: new Date().toISOString().split('T')[0],
            });
          }}
        >
          Year to date
        </div>
        <div
          className={Style.quickDateLink}
          onClick={() => {
            updateParams({
              startDate: new Date('2025-01-01').toISOString().split('T')[0],
              endDate: new Date('2025-12-31').toISOString().split('T')[0],
            });
          }}
        >
          Last year
        </div>
      </div>
      <div className={Style.formRow}>
        <div className={Style.formGroup}>
          <label className={Style.label} htmlFor="startDate">
            Start Date
            <span className={Style.required}>*</span>
          </label>
          <input
            className={Style.inputText}
            id="startDate"
            type="date"
            min="2015-01-01"
            max={endOfCurrentMonth}
            value={getParam('startDate')}
            onChange={handleInputChange}
            required
          />
        </div>

        <div className={Style.formGroup}>
          <label className={Style.label} htmlFor="endDate">
            End Date
            <span className={Style.required}>*</span>
          </label>
          <input
            className={Style.inputText}
            id="endDate"
            type="date"
            min="2015-01-01"
            max={endOfCurrentMonth}
            value={getParam('endDate')}
            onChange={handleInputChange}
            required
          />
        </div>

        <div className={Style.formGroup}>
          <label className={Style.label} htmlFor="periodicity">
            Time Interval
            <span className={Style.required}>*</span>
          </label>
          <select
            className={Style.inputSelect}
            id="periodicity"
            value={getParam('periodicity')}
            onChange={handleInputChange}
            required
          >
            {periodicityOptions.map((option, index) => (
              <option key={index} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {isDC() ? (
        <div className={Style.formRow}>
          <SearchableDropdown
            id="ward"
            label="Ward"
            options={dcWards}
            value={getMultiParam('ward')}
            onChange={handleInputChange}
            placeholder="Search ward..."
            optionalNote="(optional)"
            multiple
          />

          <SearchableDropdown
            id="cviSite"
            label="CVI Site"
            options={dcCviSites}
            value={getMultiParam('cviSite')}
            onChange={handleInputChange}
            placeholder="Search CVI site..."
            optionalNote="(optional)"
            multiple
          />
        </div>
      ) : (
        <>
          <div className={Style.areaFilterHint}>
            Select Zip Code(s), Neighborhood(s) or Council District(s)
          </div>
          <div className={Style.formRow}>
            <SearchableDropdown
              id="zipCode"
              label="ZIP Code"
              options={zipCodes}
              value={getMultiParam('zipCode')}
              onChange={(event) =>
                handleAreaFilterChange('zipCode', event.target.value)
              }
              placeholder="Search ZIP code..."
              multiple
            />

            <SearchableDropdown
              id="neighborhood"
              label="Neighborhood"
              options={neighborhoods}
              value={getMultiParam('neighborhood')}
              onChange={(event) =>
                handleAreaFilterChange('neighborhood', event.target.value)
              }
              placeholder="Search neighborhood..."
              multiple
            />

            <SearchableDropdown
              id="councilDistricts"
              label="Council District"
              options={councilDistrictOptions}
              value={getMultiParam('councilDistricts')}
              onChange={(event) =>
                handleAreaFilterChange('councilDistricts', event.target.value)
              }
              placeholder="Search district..."
              multiple
            />
          </div>
        </>
      )}
    </div>
  );

  const renderOptionalSelections = () => (
    <CollapsibleSection
      id="optional"
      title="Optional Selections"
      openSections={openSections}
      toggleSection={toggleSection}
    >
      <div className={Style.formRow}>
        <InputDropdown
          inputList={{
            FATAL: {
              label: 'Fatal',
              value: 'FATAL',
            },
            NONFATAL: {
              label: 'Non-Fatal',
              value: 'NONFATAL',
            },
          }}
          labelText="Fatal/Non-Fatal"
          name="shootingType"
          value={getParam('shootingType')}
          onChange={({ shootingType }) => {
            updateParams({ shootingType });
          }}
        />
        <InputDropdown
          inputList={{
            SINGLE: {
              label: 'Single-victim',
              value: 'single',
            },
            MULTI: {
              label: 'Multi-victim',
              value: 'multi',
            },
          }}
          labelText="Single/Multi-victim"
          name="incidentType"
          value={getParam('incidentType')}
          onChange={({ incidentType }) => {
            updateParams({ incidentType });
          }}
        />
        <div style={{ display: 'flex', gap: '10px' }}>
          <InputDropdown
            inputList={TIME_PICKER_OPTIONS}
            labelText="Time Min"
            name="time_min"
            value={parseTimePickerValue(getParam('time_min'))}
            onChange={({ time_min }) =>
              handleTimePickerChange('time_min', time_min)
            }
          />
          <InputDropdown
            inputList={TIME_PICKER_OPTIONS}
            labelText="Time Max"
            name="time_max"
            value={parseTimePickerValue(getParam('time_max'))}
            onChange={({ time_max }) =>
              handleTimePickerChange('time_max', time_max)
            }
          />
        </div>
        <InputDropdown
          inputList={{
            M: {
              label: 'Male',
              value: 'M',
            },
            F: {
              label: 'Female',
              value: 'F',
            },
          }}
          labelText="Victim Sex"
          name="sex"
          value={getParam('sex')}
          onChange={({ sex }) => {
            updateParams({ sex });
          }}
        />
        <InputMultiDropdown
          inputList={{
            BLACK: {
              label: 'Black',
              value: 'BLACK',
            },
            WHITE: {
              label: 'White',
              value: 'WHITE',
            },
            HISPANIC: {
              label: 'Hispanic',
              value: 'HISPANIC',
            },
            ASIAN: {
              label: 'Asian',
              value: 'ASIAN',
            },
            OTHER: {
              label: 'Other',
              value: 'OTHER',
            },
            UNSPECIFIED: {
              label: 'Unspecified',
              value: 'UNSPECIFIED',
            },
          }}
          labelText="Victim Race"
          name="race"
          values={getArrayParam('race')}
          onChange={({ race }) => {
            console.log('raw race from component:', race);
            let raceArray = [];
            if (typeof race === 'string') {
              raceArray = race
                .split(',')
                .map((r) => r.trim())
                .filter(Boolean);
            } else if (Array.isArray(race)) {
              raceArray = race
                .flatMap((r) => {
                  if (typeof r === 'string' && r.includes(',')) {
                    return r.split(',').map((s) => s.trim());
                  }
                  return r;
                })
                .filter(Boolean);
            }
            raceArray = [...new Set(raceArray)];
            updateParams({ race: raceArray });
          }}
        />
        <InputRange
          inputList={{
            AGE: { min: 0, max: 100, step: 1, formatLabel: RangeFormat.age },
          }}
          labelText="Victim Age"
          name="age"
          value={{
            min: Number(getParam('age_min')) || 0,
            max: Number(getParam('age_max')) || 100,
          }}
          onChange={({ age }) => handleRangeChange('age', age)}
        />
      </div>
    </CollapsibleSection>
  );

  const renderAdvancedParameters = () => (
    <CollapsibleSection
      id="advanced"
      title="Advanced Parameters"
      openSections={openSections}
      toggleSection={toggleSection}
    >
      <div className={Style.formRow}>
        {isDC() ? (
          <InputRange
            inputList={{
              BLOCKS: {
                min: 0,
                max: 100,
                step: 0.5,
                formatLabel: RangeFormat.number,
              },
            }}
            labelText="# Blocks From Another Shooting"
            name="blocksFromShooting"
            value={{
              min: Number(getParam('blocksFromShooting_min')) || 0,
              max: Number(getParam('blocksFromShooting_max')) || 100,
            }}
            onChange={({ blocksFromShooting }) =>
              handleRangeChange('blocksFromShooting', blocksFromShooting)
            }
          />
        ) : (
          <>
            <InputRange
              inputList={{
                AGE: {
                  min: 0,
                  max: 100,
                  step: 1,
                  formatLabel: RangeFormat.number,
                },
              }}
              labelText="# Blocks From School"
              name="blocksFromSchool"
              value={{
                min: Number(getParam('blocksFromSchool_min')) || 0,
                max: Number(getParam('blocksFromSchool_max')) || 100,
              }}
              onChange={({ blocksFromSchool }) =>
                handleRangeChange('blocksFromSchool', blocksFromSchool)
              }
            />

            <SearchableDropdown
              id="schoolName"
              label="School"
              options={schools}
              value={getParam('schoolName')}
              onChange={handleSchoolChange}
              placeholder="Search school..."
              selectOnly
              showAllOption={false}
            />
          </>
        )}
      </div>
    </CollapsibleSection>
  );

  const handleReset = (e) => {
    e.preventDefault();

    const params = new URLSearchParams();
    params.set('jurisdiction', 'PHILADELPHIA');
    setSearchParams(params);

    setChartData(null);
    setPptInfo({});
    setSubscribeConfig(null);
    setError('');
    setMessage('');
  };

  return (
    <div className={Style.chartFormContainer}>
      <form onSubmit={handleSubmit} className={Style.chartForm}>
        <div className={Style.formHeader}>
          <h2>PERFORM Reports</h2>
          <p className={Style.formSubtitle}>
            Produce trend reports from public datasets
          </p>
        </div>

        {error && (
          <div className={Style.errorMessage}>
            <span className={Style.errorIcon}>!</span>
            {error}
          </div>
        )}

        {message &&
          (message.includes('Error') ? (
            <div className={Style.partialSuccessMessage}>{message}</div>
          ) : (
            <div className={Style.successMessage}>{message}</div>
          ))}

        <div className={Style.section}>
          <div className={Style.titleContainer}>
            <h3 className={Style.sectionTitle}>Dashboard Report Parameters</h3>
            <button
              type="button"
              className={Style.resetButton}
              onClick={handleReset}
            >
              Reset
            </button>
          </div>

          <div className={Style.inputs}>
            {renderDashboardFields()}
            {!isDC() && renderOptionalSelections()}
            {renderAdvancedParameters()}
          </div>
        </div>

        <div className={Style.formActions}>
          <div
            type="submit"
            disabled={isLoading}
            className={Style.submitButton}
            onClick={handleSubmit}
          >
            Generate Report
          </div>
        </div>
      </form>

      {isLoading && (
        <div>
          <Spinner />
        </div>
      )}

      {chartData && (
        <ChartDisplay
          chartData={chartData}
          error={error}
          pptInfo={pptInfo}
          subscribeConfig={subscribeConfig}
        />
      )}
    </div>
  );
};

export default ChartForm;
